import { useState, useEffect, useRef } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import {
    ArrowLeft,
    User,
    Plus,
    Trash2,
    Calendar,
    Package,
    Info,
    Loader2,
    AlertCircle,
    ChevronDown,
    Search
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

import AdminProductService from "../../services/admin/AdminProductService";
import AdminInvoiceService from "../../services/admin/AdminInvoiceService";
import { formatPrice } from "../../utils/productUtils";
import { useNotification } from "../../context/NotificationContext";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";

function SearchableProductSelect({ products, value, onChange, placeholder, error, selectedProductIds = [] }) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef(null);

    const selectedProduct = products.find(p => p.id === value);

    // Filter products based on search
    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && filteredProducts.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            onChange(filteredProducts[0].id);
            setIsOpen(false);
            setSearch("");
        } else if (e.key === "Escape") {
            setIsOpen(false);
            setSearch("");
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <div
                className={`w-full px-3 py-2 bg-white dark:bg-[#1A1A1A] border ${error ? 'border-red-400' : 'border-[#E5E7EB] dark:border-[#333333]'} rounded-lg text-sm flex items-center justify-between cursor-pointer focus-within:ring-1 focus-within:ring-[#62397f] transition-all`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isOpen ? (
                        <>
                            <Search size={14} className="text-gray-400 flex-shrink-0" />
                            <input
                                className="bg-transparent border-none outline-none w-full text-sm"
                                placeholder="Type to search..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </>
                    ) : (
                        <span className={`truncate ${selectedProduct ? "text-[#111111] dark:text-white font-medium" : "text-[#9CA3AF]"}`}>
                            {selectedProduct ? selectedProduct.name : placeholder}
                        </span>
                    )}
                </div>
                <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#333333] rounded-xl shadow-xl max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-200">
                    {filteredProducts.length > 0 ? (
                        filteredProducts.map(p => {
                            const isAlreadySelected = selectedProductIds.includes(p.id) && p.id !== value;
                            return (
                                <div
                                    key={p.id}
                                    className={`px-4 py-2.5 text-sm flex items-center justify-between hover:bg-[#F3F4F6] dark:hover:bg-[#262626] cursor-pointer transition-colors ${p.id === value ? "bg-purple-50 dark:bg-purple-900/10 text-purple-600 font-medium" : isAlreadySelected ? "text-gray-400 opacity-60" : "text-[#4B5563] dark:text-[#D1D5DB]"}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onChange(p.id);
                                        setIsOpen(false);
                                        setSearch("");
                                    }}
                                >
                                    <span className="truncate">{p.name}</span>
                                    {isAlreadySelected && (
                                        <span className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-500 font-normal">Added</span>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 italic text-center">No products found</div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function CreateInvoicePage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { showNotification } = useNotification();
    const { hasPermission } = useAdminPermissions();

    const prefilledCustomer = location.state?.prefilledCustomer || null;
    const isCustomerLocked = !!prefilledCustomer;

    const [products, setProducts] = useState([]);
    const [items, setItems] = useState([
        { id: 1, product: "", quantity: 1, price: 0 },
    ]);
    const [customer, setCustomer] = useState({
        name: prefilledCustomer?.name || "",
        phone: prefilledCustomer?.phone || "",
        email: prefilledCustomer?.email || "",
        address: prefilledCustomer?.address || "",
    });
    const [dates, setDates] = useState({
        start: "",
        end: "",
    });
    const [logisticsFee, setLogisticsFee] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [settings, setSettings] = useState({
        service_charge_percent: 10,
        vat_percent: 7.5
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);

    useEffect(() => {
        if (!hasPermission("admin")) {
            showNotification({
                type: "failed",
                title: "Access Restricted",
                message: "You do not have permission to generate invoices.",
            });
            navigate("/admin/dashboard");
        }
    }, [hasPermission, navigate, showNotification]);

    useEffect(() => {
        fetchProducts();
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const result = await AdminInvoiceService.getSettings();
            if (result.status === "success" && result.data) {
                setSettings({
                    service_charge_percent: result.data.service_charge_percent,
                    vat_percent: result.data.vat_percent
                });
            }
        } catch (error) {
            console.error("Failed to fetch invoice settings", error);
        }
    };

    const fetchProducts = async () => {
        try {
            const data = await AdminProductService.list({ limit: 1000, status: "active" });
            const productList = data.items || data.data || [];
            setProducts(Array.isArray(productList) ? productList : []);
        } catch (error) {
            console.error("Failed to fetch products", error);
            showNotification({
                type: "failed",
                title: "Load Error",
                message: "Failed to load products. Please refresh the page."
            });
        }
    };

    const addItem = () => {
        setItems([
            ...items,
            { id: Date.now(), product: "", quantity: 1, price: 0 },
        ]);
    };

    const removeItem = (id) => {
        if (items.length > 1) {
            setItems(items.filter((item) => item.id !== id));
        }
    };

    const updateItem = (id, field, value) => {
        let isNowDuplicate = false;
        setItems(prevItems => {
            return prevItems.map((item) => {
                if (item.id !== id) return item;
                const updates = { [field]: value };
                if (field === "product") {
                    const selectedProduct = products.find(p => p.id === value);
                    if (selectedProduct) {
                        updates.price = parseFloat(selectedProduct.price);
                    }

                    // Check for duplicates
                    if (value !== "" && prevItems.some(i => i.id !== id && i.product === value)) {
                        isNowDuplicate = true;
                    }
                }
                return { ...item, ...updates };
            });
        });

        if (field === "product") {
            setErrors(prev => {
                const next = { ...prev };
                const errorKey = `item_${id}`;

                if (isNowDuplicate) {
                    next[errorKey] = "This product is already in the list.";
                } else if (next[errorKey] && next[errorKey].includes("already in the list")) {
                    delete next[errorKey];
                }
                return next;
            });
        }
    };

    const clearFieldError = (field) => {
        if (errors[field]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const handleDateChange = (field, value) => {
        // Standard HTML date input returns YYYY-MM-DD
        const parts = value.split('-');
        if (parts[0] && parts[0].length > 4) {
            parts[0] = parts[0].slice(0, 4);
            value = parts.join('-');
        }
        setDates(prev => ({ ...prev, [field]: value }));
        clearFieldError(field);
    };

    const calculateSubtotal = (item) => item.quantity * item.price;
    const itemsTotal = items.reduce(
        (acc, item) => acc + calculateSubtotal(item),
        0,
    );

    const serviceCharge = itemsTotal * (parseFloat(settings.service_charge_percent) / 100);
    const vat = (itemsTotal + serviceCharge + (parseFloat(logisticsFee) || 0)) * (parseFloat(settings.vat_percent) / 100);
    const totalAmount = itemsTotal + serviceCharge + (parseFloat(logisticsFee) || 0) + vat - (parseFloat(discount) || 0);

    const validateForm = () => {
        const newErrors = {};

        if (!customer.name.trim()) newErrors.name = "Customer name is required.";
        if (!customer.phone.trim()) newErrors.phone = "Phone number is required.";
        if (!customer.address.trim()) newErrors.address = "Address is required.";

        if (customer.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(customer.email.trim())) {
            newErrors.email = "Please enter a valid email address.";
        }

        if (!dates.start) newErrors.start = "Start date is required.";
        if (!dates.end) newErrors.end = "Return date is required.";
        if (dates.start && dates.end && dates.end < dates.start) {
            newErrors.end = "Return date cannot be before start date.";
        }

        const productCounts = {};
        items.forEach((item) => {
            const itemErrors = [];
            if (!item.product) {
                itemErrors.push("Select a product");
            } else {
                productCounts[item.product] = (productCounts[item.product] || 0) + 1;
                if (productCounts[item.product] > 1) {
                    itemErrors.push("Duplicate product");
                }
            }

            if (item.quantity < 1) itemErrors.push("Qty must be ≥ 1");

            if (itemErrors.length > 0) {
                if (newErrors[`item_${item.id}`]) {
                    newErrors[`item_${item.id}`] += " " + itemErrors.join(". ") + ".";
                } else {
                    newErrors[`item_${item.id}`] = itemErrors.join(". ") + ".";
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreateInvoice = async () => {
        if (submittingRef.current) return;

        if (!validateForm()) {
            showNotification({
                type: "failed",
                title: "Validation Error",
                message: "Please fill in all required fields before submitting."
            });
            return;
        }

        submittingRef.current = true;
        setIsSubmitting(true);

        try {
            const payload = {
                customer: prefilledCustomer ? { ...customer, id: prefilledCustomer.id } : customer,
                dates,
                items: items.map(item => ({
                    product: item.product,
                    quantity: item.quantity,
                    price: item.price
                })),
                logistics_fee: parseFloat(logisticsFee) || 0,
                service_charge: serviceCharge,
                vat_amount: vat,
                discount: parseFloat(discount) || 0,
                total_amount: totalAmount
            };

            const response = await AdminInvoiceService.create(payload);
            const invoiceData = response?.data?.data;
            const invoiceNumber = invoiceData?.invoice_number || "N/A";
            const invoiceId = invoiceData?.id;

            showNotification({
                type: "success",
                title: "Invoice Generated",
                message: `Invoice ${invoiceNumber} has been created successfully.`
            });

            if (invoiceId) {
                navigate(`/admin/invoices/${invoiceId}`, { state: { fromCreate: true } });
            } else {
                navigate("/admin/invoices");
            }
        } catch (error) {
            const msg = error?.response?.data?.message || "Failed to create invoice. Please try again.";
            showNotification({
                type: "failed",
                title: "Invoice Error",
                message: msg
            });
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const FieldError = ({ field }) => {
        if (!errors[field]) return null;
        return (
            <p className="flex items-center gap-1 mt-1.5 text-xs text-red-500 font-inter">
                <AlertCircle size={12} className="flex-shrink-0" />
                {errors[field]}
            </p>
        );
    };

    const inputClass = (field) =>
        `w-full px-4 py-2.5 bg-[#F9FAFB] dark:bg-[#262626] border ${errors[field]
            ? "border-red-400 focus:ring-red-300"
            : "border-[#E5E7EB] dark:border-[#333333] focus:ring-[#62397f]"
        } rounded-xl text-sm focus:ring-2 outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-[#1A1A1A]`;

    return (
        <AdminLayout>
            <div className="p-4 lg:p-8 max-w-[1200px] mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-[#F3F4F6] dark:hover:bg-[#262626] rounded-xl transition-colors"
                    >
                        <ArrowLeft
                            size={20}
                            className="text-[#6B7280] dark:text-[#A0A0A0]"
                        />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-[#111111] dark:text-white font-poppins">
                            Generate Invoice
                        </h1>
                        <p className="text-[#666666] dark:text-[#A0A0A0] text-sm font-inter">
                            Create a new rental agreement and invoice
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Form Area */}
                    <div className="lg:col-span-2 space-y-8 pb-20">
                        {/* Customer Details */}
                        <section className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/10 rounded-xl flex items-center justify-center text-blue-600">
                                    <User size={20} />
                                </div>
                                <h2 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                                    Customer Details
                                </h2>
                            </div>

                            {isCustomerLocked && (
                                <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 rounded-xl">
                                    <Info size={14} className="text-purple-600 flex-shrink-0" />
                                    <p className="text-xs text-purple-700 dark:text-purple-300">Customer details are pre-filled and locked. Use the back button to return to the customer profile.</p>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g. John Doe"
                                        value={customer.name}
                                        disabled={isCustomerLocked}
                                        onChange={(e) => {
                                            setCustomer({ ...customer, name: e.target.value });
                                            clearFieldError("name");
                                        }}
                                        className={inputClass("name")}
                                    />
                                    <FieldError field="name" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase">
                                        Phone Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        placeholder="+234 802 000 0000"
                                        value={customer.phone}
                                        disabled={isCustomerLocked}
                                        onChange={(e) => {
                                            setCustomer({ ...customer, phone: e.target.value });
                                            clearFieldError("phone");
                                        }}
                                        className={inputClass("phone")}
                                    />
                                    <FieldError field="phone" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase">
                                        Email <span className="text-[#9CA3AF] text-[10px] normal-case">(Optional)</span>
                                    </label>
                                    <input
                                        type="email"
                                        placeholder="john@example.com"
                                        value={customer.email}
                                        disabled={isCustomerLocked}
                                        onChange={(e) => {
                                            setCustomer({ ...customer, email: e.target.value });
                                            clearFieldError("email");
                                        }}
                                        className={inputClass("email")}
                                    />
                                    <FieldError field="email" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase">
                                        Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="123 Street, City"
                                        value={customer.address}
                                        disabled={isCustomerLocked}
                                        onChange={(e) => {
                                            setCustomer({ ...customer, address: e.target.value });
                                            clearFieldError("address");
                                        }}
                                        className={inputClass("address")}
                                    />
                                    <FieldError field="address" />
                                </div>
                            </div>
                        </section>

                        {/* Rental Items */}
                        <section className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/10 rounded-xl flex items-center justify-center text-purple-600">
                                        <Package size={20} />
                                    </div>
                                    <h2 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                                        Rental Items
                                    </h2>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {items.map((item) => (
                                    <div
                                        key={item.id}
                                        className={`p-4 bg-[#F9FAFB] dark:bg-[#262626] rounded-2xl border ${errors[`item_${item.id}`]
                                            ? "border-red-300 dark:border-red-800"
                                            : "border-[#E5E7EB] dark:border-[#333333]"
                                            } relative group`}
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                            <div className="md:col-span-5 space-y-1.5">
                                                <label className="text-[10px] font-bold text-[#9CA3AF] uppercase">
                                                    Product <span className="text-red-500">*</span>
                                                </label>
                                                <SearchableProductSelect
                                                    products={products}
                                                    value={item.product}
                                                    onChange={(val) => updateItem(item.id, "product", val)}
                                                    placeholder="Select a product"
                                                    error={errors[`item_${item.id}`]}
                                                    selectedProductIds={items.map(i => i.product).filter(p => p)}
                                                />
                                            </div>
                                            <div className="md:col-span-3 space-y-1.5">
                                                <label className="text-[10px] font-bold text-[#9CA3AF] uppercase">
                                                    Qty <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.quantity === 0 ? "" : item.quantity}
                                                    min="1"
                                                    onChange={(e) => {
                                                        let val = e.target.value;

                                                        // Prevent negative or zero as a persistent value if they type
                                                        if (val === "") {
                                                            updateItem(item.id, "quantity", 0);
                                                            return;
                                                        }

                                                        const numVal = parseInt(val);
                                                        updateItem(
                                                            item.id,
                                                            "quantity",
                                                            isNaN(numVal) ? 0 : numVal,
                                                        );
                                                    }}
                                                    className="w-full px-3 py-2 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#333333] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#62397f]"
                                                />
                                            </div>

                                            <div className="md:col-span-4 space-y-1.5">
                                                <label className="text-[10px] font-bold text-[#9CA3AF] uppercase">
                                                    Unit Price
                                                </label>
                                                <div className="relative">
                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                                                        ₦
                                                    </span>
                                                    <input
                                                        type="number"
                                                        value={item.price}
                                                        readOnly
                                                        className="w-full pl-7 pr-3 py-2 bg-[#F3F4F6] dark:bg-[#333333] border border-[#E5E7EB] dark:border-[#333333] rounded-lg text-sm outline-none cursor-not-allowed text-gray-500"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center">
                                            <div>
                                                <p className="text-xs text-[#6B7280]">
                                                    Subtotal:{" "}
                                                    <span className="font-bold text-[#111111] dark:text-white">
                                                        ₦{formatPrice(calculateSubtotal(item))}
                                                    </span>
                                                </p>
                                                {errors[`item_${item.id}`] && (
                                                    <p className="flex items-center gap-1 mt-1 text-xs text-red-500 font-inter">
                                                        <AlertCircle size={12} className="flex-shrink-0" />
                                                        {errors[`item_${item.id}`]}
                                                    </p>
                                                )}
                                            </div>
                                            {items.length > 1 && (
                                                <button
                                                    onClick={() => removeItem(item.id)}
                                                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={addItem}
                                className="mt-6 flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-[#E5E7EB] dark:border-[#333333] text-[#6B7280] dark:text-[#A0A0A0] hover:border-[#62397f] hover:text-[#62397f] dark:hover:border-[#62397f] dark:hover:text-[#62397f] rounded-2xl text-sm font-bold transition-all"
                            >
                                <Plus size={18} />
                                Add Another Item
                            </button>
                        </section>

                        {/* Rental Dates */}
                        <section className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-orange-50 dark:bg-orange-900/10 rounded-xl flex items-center justify-center text-orange-600">
                                    <Calendar size={20} />
                                </div>
                                <h2 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                                    Rental Dates
                                </h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase">
                                        Start Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className={inputClass("start")}
                                        value={dates.start}
                                        onChange={(e) => handleDateChange("start", e.target.value)}
                                        max="9999-12-31"
                                    />
                                    <FieldError field="start" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase">
                                        Expected Return Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className={inputClass("end")}
                                        value={dates.end}
                                        onChange={(e) => handleDateChange("end", e.target.value)}
                                        max="9999-12-31"
                                    />
                                    <FieldError field="end" />
                                </div>
                            </div>
                        </section>

                    </div>

                    {/* Sticky Summary Panel */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-8 bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-lg overflow-hidden">
                            <div className="p-6 bg-[#F9FAFB] dark:bg-[#262626] border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
                                <h3 className="font-bold text-[#111111] dark:text-white font-poppins">
                                    Invoice Summary
                                </h3>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-[#6B7280] dark:text-[#A0A0A0]">
                                        Total Items
                                    </span>
                                    <span className="font-semibold text-[#111111] dark:text-white">
                                        {items.length}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-[#6B7280] dark:text-[#A0A0A0]">
                                        Rental Subtotal
                                    </span>
                                    <span className="font-semibold text-[#111111] dark:text-white">
                                        ₦{formatPrice(itemsTotal)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-[#6B7280] dark:text-[#A0A0A0]">
                                        Service Charge ({settings.service_charge_percent}%)
                                    </span>
                                    <span className="font-semibold text-[#111111] dark:text-white">
                                        ₦{formatPrice(serviceCharge)}
                                    </span>
                                </div>
                                <div className="space-y-1.5">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-[#6B7280] dark:text-[#A0A0A0]">
                                            Logistics Fee
                                        </span>
                                        <div className="relative w-32">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                                                ₦
                                            </span>
                                            <input
                                                type="number"
                                                value={logisticsFee === 0 ? "" : logisticsFee}
                                                placeholder="0.00"
                                                onChange={(e) => {
                                                    let val = e.target.value;
                                                    if (val.length > 1 && val.startsWith('0')) {
                                                        val = val.replace(/^0+/, '');
                                                    }
                                                    setLogisticsFee(val);
                                                }}
                                                className="w-full pl-5 pr-2 py-1 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#333333] rounded-lg text-xs outline-none focus:ring-1 focus:ring-[#62397f] text-right font-semibold"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-[#6B7280] dark:text-[#A0A0A0]">
                                            VAT ({settings.vat_percent}%)
                                        </span>
                                        <span className="font-semibold text-[#111111] dark:text-white">
                                            ₦{formatPrice(vat)}
                                        </span>
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-red-500 font-medium">
                                            Discount
                                        </span>
                                        <div className="relative w-32">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                                                ₦
                                            </span>
                                            <input
                                                type="number"
                                                value={discount === 0 ? "" : discount}
                                                placeholder="0.00"
                                                onChange={(e) => {
                                                    let val = e.target.value;
                                                    if (val.length > 1 && val.startsWith('0')) {
                                                        val = val.replace(/^0+/, '');
                                                    }
                                                    setDiscount(val);
                                                }}
                                                className="w-full pl-5 pr-2 py-1 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#333333] rounded-lg text-xs outline-none focus:ring-1 focus:ring-red-400 text-right font-semibold text-red-500"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-dashed border-gray-200 dark:border-gray-700 flex justify-between items-center">
                                    <span className="text-lg font-bold text-[#111111] dark:text-white">
                                        Grand Total
                                    </span>
                                    <span className="text-2xl font-black text-[#62397f] tracking-tight">
                                        ₦{formatPrice(totalAmount)}
                                    </span>
                                </div>

                                <div className="mt-8 space-y-3">
                                    <button
                                        onClick={handleCreateInvoice}
                                        disabled={isSubmitting}
                                        className="w-full py-4 bg-[#62397f] hover:bg-[#7C3AED] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl font-bold shadow-lg shadow-purple-100 dark:shadow-none transition-all active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 size={20} className="animate-spin" />
                                                Generating...
                                            </>
                                        ) : (
                                            "Generate Invoice"
                                        )}
                                    </button>
                                </div>

                                <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-xl mt-6">
                                    <Info
                                        size={16}
                                        className="text-blue-600 mt-0.5 flex-shrink-0"
                                    />
                                    <p className="text-[11px] text-blue-700 dark:text-blue-400 leading-normal">
                                        Invoices are generated with "Generated" status. You can
                                        update the lifecycle after creation.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
